import React, { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronDown, CloudOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getActiveAuthToken, getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { getSavedDeliveryLabel } from '@/api/deliveries';
import DeleteSavedDeliveryButton from './DeleteSavedDeliveryButton';
import { deliveryPrincipalKey } from '@/utils/deliveryDraftStore';
import { preparedPhotoDeletion } from '@/utils/preparedPhotoDeletion';

const validId = (id) => typeof id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(id);
const missing = (value) => value == null || value === '';
const validDate = (value) => {
  if (missing(value)) return true;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00`);
  return Number.isFinite(date.getTime()) && date.getFullYear() === Number(value.slice(0, 4))
    && date.getMonth() + 1 === Number(value.slice(5, 7)) && date.getDate() === Number(value.slice(8));
};
const validLabel = (result, id) => result?.order_id === id
  && (missing(result.internal_order_number) || typeof result.internal_order_number === 'string')
  && validDate(result.delivery_date);

function SavedDeliveryRows({ opening, owns, onDeleted, onReviewPrepared, onPreparedDeleted }) {
  const [rows, setRows] = useState([]);
  const retry = useRef(null);
  useEffect(() => {
    let active = true;
    let running = 0;
    const controllers = new Set();
    const entries = opening.ids.map((id) => ({ state: validId(id) && opening.token ? 'loading' : 'unavailable' }));
    const queue = opening.ids.flatMap((id, index) => validId(id) && opening.token ? [index] : []);
    const current = () => active && owns();
    const publish = () => { if (current()) setRows([...entries]); };
    const pump = () => {
      while (current() && running < 3 && queue.length) {
        const index = queue.shift();
        const controller = new AbortController();
        controllers.add(controller);
        running += 1;
        const id = opening.ids[index];
        getSavedDeliveryLabel(id, { token: opening.token, signal: controller.signal }).then((result) => {
          if (!current()) return;
          entries[index] = validLabel(result, id) ? { state: 'ready', label: result } : { state: 'error' };
          publish();
        }).catch((error) => {
          if (!current()) return;
          const denied = [401, 403, 404, 422].includes(error?.response?.status);
          entries[index] = { state: denied ? 'unavailable' : 'error' };
          publish();
        }).finally(() => {
          controllers.delete(controller);
          running -= 1;
          pump();
        });
      }
    };
    retry.current = () => {
      if (!current()) return;
      entries.forEach((entry, index) => {
        if (entry.state === 'error') {
          entries[index] = { state: 'loading' };
          queue.push(index);
        }
      });
      publish();
      pump();
    };
    publish();
    pump();
    return () => {
      active = false;
      retry.current = null;
      controllers.forEach((controller) => controller.abort());
    };
  }, [opening, owns]);

  return <>
    <p className="text-xs text-blue-100 mb-2">Your saved work on this device.</p>
    {!opening.token && <p role="alert">Sign in again to read saved delivery details. Local deletion is still available.</p>}
    <ul className="space-y-2" aria-label="Saved delivery details">
      {opening.ids.map((_id, index) => {
        const row = rows[index];
        return <li key={index} className="rounded-lg bg-blue-700 p-3 break-words text-sm">
          {row?.state === 'ready' ? <>
            <p>Order number: <strong>{row.label.internal_order_number?.trim() || 'Unavailable'}</strong></p>
            <p>Delivery date: {missing(row.label.delivery_date) ? 'Unavailable'
              : new Date(`${row.label.delivery_date}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </> : <p>Saved delivery {index + 1} — {row && row.state !== 'loading' ? 'Details unavailable' : 'Loading details…'}</p>}
          {!opening.prepared[index] && <p className="mt-2 text-sm">
            This older saved copy cannot be resent. The app cannot verify which contents reached the server.
            Opening this notice does not send or delete the copy. It may block other work on the same order.
          </p>}
          {opening.prepared[index] ? <>
            <p>{opening.prepared[index].status === 'draft' ? 'Unfinished delivery — not sent.' : opening.prepared[index].operation === 'failure' && opening.prepared[index].status === 'accepted' ? 'Failure reported. Photos remain on this device.' : 'We couldn’t confirm this delivery. Open it to check again.'}</p>
            <button type="button" className="mt-2 rounded-lg border px-3 py-2" onClick={() => { if (owns()) onReviewPrepared?.(opening.prepared[index]); }}
              data-testid={`review-prepared-${opening.prepared[index].kind}-${opening.ids[index]}`}>Open saved work</button>
            {opening.prepared[index].status === 'draft' && !opening.prepared[index].memoryOnly && <DeleteSavedDeliveryButton
              orderId={opening.ids[index]} owns={owns} onDeleted={onPreparedDeleted}
              deletion={preparedPhotoDeletion(deliveryPrincipalKey(opening.binding), opening.prepared[index])}
              label={`Saved ${opening.prepared[index].kind} photo draft — ${row?.state === 'ready'
                ? `Order number: ${row.label.internal_order_number?.trim() || 'Unavailable'} — Delivery date: ${row.label.delivery_date || 'Unavailable'}`
                : `Saved delivery ${index + 1} — Details unavailable`}`} />}
          </> : opening.prepared.some((copy) => copy && copy.orderId === opening.keys[index])
            ? <p className="mt-2 text-sm">A prepared photo copy also exists. Review that work before deleting this older copy. This older copy cannot be resent.</p>
            : <DeleteSavedDeliveryButton orderId={opening.keys[index]} owns={owns} onDeleted={onDeleted}
            label={row?.state === 'ready'
              ? `Order number: ${row.label.internal_order_number?.trim() || 'Unavailable'} — Delivery date: ${row.label.delivery_date || 'Unavailable'}`
              : `Saved delivery ${index + 1} — Details unavailable`} />}
        </li>;
      })}
    </ul>
    {rows.some((row) => row.state === 'error') && <div className="mt-2 text-sm">
      <p>Could not read some details. Check your connection and retry.</p>
      <button type="button" className="mt-2 rounded-lg border border-white px-3 py-2" onClick={() => retry.current?.()}>Retry details</button>
    </div>}
  </>;
}

const NO_PREPARED = [];
export default function SavedDeliveryReviewList({ records, onDeleted, preparedRecords = NO_PREPARED, onReviewPrepared, onPreparedDeleted }) {
  const { sessionBinding } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const regionId = useId();
  // Any refreshed record snapshot retires consent, even for the same IDs.
  // Keep the actual local key separate from cloud lookup eligibility; no media.
  const opening = useMemo(() => ({ binding: sessionBinding, token: getActiveAuthToken(),
    ids: [...records, ...preparedRecords].map((record) => validId(record?.orderId) ? record.orderId : null),
    keys: records.map((record) => typeof record?.orderId === 'string' ? record.orderId : null),
    prepared: [...records.map(() => null), ...preparedRecords],
  }), [sessionBinding, records, preparedRecords]);
  const latest = useRef(null);
  latest.current = opening;
  const owns = useMemo(() => () => latest.current === opening && !!opening.binding?.user?.id
    && opening.binding.source === 'cloud' && getActiveSessionRevision() === opening.binding.revision, [opening]);
  const [expandedOpening, setExpandedOpening] = useState(null);
  const expanded = expandedOpening === opening;
  const visible = owns() && revision === opening.binding?.revision;
  if (!visible || !opening.ids.length) return null;
  return <section className="mx-4 mt-3 rounded-xl bg-blue-600 text-white" data-testid="unsent-deliveries-banner">
    <button type="button" className="w-full p-3 flex items-center gap-3 text-left rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      aria-expanded={expanded} aria-controls={regionId} onClick={() => { if (owns()) setExpandedOpening(expanded ? null : opening); }}>
      <CloudOff className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="flex-1 text-sm font-medium">{opening.ids.length} saved {opening.ids.length === 1 ? 'delivery' : 'deliveries'}</span>
      <ChevronDown className={`h-5 w-5 shrink-0 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
    </button>
    <div id={regionId} className={`grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
      <div className="overflow-hidden min-h-0">
        {expanded && <div className="px-3 pb-3"><SavedDeliveryRows opening={opening} owns={owns} onDeleted={onDeleted} onReviewPrepared={onReviewPrepared} onPreparedDeleted={onPreparedDeleted} /></div>}
      </div>
    </div>
  </section>;
}
